import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormGroupDirective, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { LoginLibService } from '@ebootis/login';
import { ConfigurationService } from 'projects/zbs-app/src/app/components/configuration-select/configuration.service';
import { Configuration } from 'projects/zbs-app/src/app/services/Ebootis.Queries/Configuration';
import { CustomerByOid } from 'projects/zbs-app/src/app/services/Ebootis.Queries/CustomerByOid';
import { IZbsApp } from 'projects/zbs-app/src/app/services/Ebootis.ServiceProxies/IZbsApp';
import { LogisticsAreaType } from 'projects/zbs-app/src/app/services/Ebootis.Shared/LogisticsAreaType';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LogisticAreaService } from '../../../shared/logistic-area/logistic-area.service';
import { CustomerService } from '../../customer.service';
import { CustomerEditI18n } from './CustomerEditI18n';

@Component({
	selector: 'app-customer-edit',
	templateUrl: './customer-edit.component.html',
	styleUrls: ['./customer-edit.component.scss'],
	providers: [CustomerEditI18n]
})
export class CustomerEditComponent implements OnInit, OnDestroy {

	@Input() set forCompany(p_forCompany: boolean) {
		this.m_forCompany = p_forCompany;
	}

	get forCompany(): boolean {
		return this.m_forCompany;
	}

	@Input() set selectedCustomer(p_customer: string) {
		this.m_selectedCustomerOid = p_customer;
		// Wenn Kunde in Tabelle ausgewählt, Felder vorausfüllen
		if (this.m_selectedCustomerOid != '') {
			this.m_customerService.searchCustomerByOid({ customer: this.m_selectedCustomerOid }).pipe(
				takeUntil(this.m_subscriptionsSubject)
			).subscribe({
				next: (data: CustomerByOid.Result) => {
					this.m_selectedCustomer = data;
					this.m_customerForm.get(this.AT_LEAST_ONE + '.' + this.LAST_NAME).setValue(data.lastName);
					this.m_customerForm.get(this.AT_LEAST_ONE + '.' + this.COMPANY_NAME1).setValue(data.companyName1);
					this.m_customerForm.get(this.AT_LEAST_ONE + '.' + this.COMPANY_NAME2).setValue(data.companyName2);
					this.m_customerForm.get(this.LOGISTIC_AREA).setValue(data.logisticsAreaProxyOid);
					this.m_customerForm.get(this.ID).setValue(data.id);
					this.m_customerForm.get(this.TITLE).setValue(data.title);
					this.m_customerForm.get(this.FIRST_NAME).setValue(data.firstName);
					this.m_customerForm.get(this.EMAIL).setValue(data.email);
					this.m_customerForm.get(this.PHONE).setValue(data.phoneNumber);
					this.m_customerForm.get(this.STREET).setValue(data.street);
					this.m_customerForm.get(this.ZIP).setValue(data.zip);
					this.m_customerForm.get(this.CITY).setValue(data.city);
					this.m_customerForm.get(this.COUNTRY).setValue(data.country);
					this.m_customerForm.get(this.HIERARCHY_NODES_1).setValue(data.hierarchyNode1);
					this.m_customerForm.get(this.HIERARCHY_NODES_2).setValue(data.hierarchyNode2);
					this.m_customerForm.get(this.HIERARCHY_NODES_3).setValue(data.hierarchyNode3);
					this.m_customerForm.get(this.HIERARCHY_NODES_4).setValue(data.hierarchyNode4);
					this.m_customerForm.get(this.DISTRIBUTION_AMOUNT_1).setValue(data.distributionAmount1);
					this.m_customerForm.get(this.DISTRIBUTION_AMOUNT_2).setValue(data.distributionAmount2);
					this.m_customerForm.get(this.OWNER).setValue(data.owner?.fullName);
					this.m_customerForm.get(this.PUBLIC_ADDRESS).setValue(data.isPublic);
					if (this.m_configuration?.userOid == data.owner?.oid && this.m_configuration?.accessAllAddresses)
						this.m_customerForm.get(this.PUBLIC_ADDRESS).enable();
					else this.m_customerForm.get(this.PUBLIC_ADDRESS).disable();
				},
				error: (error) => {
					this.m_displayError = error;
					this.m_messageHandlerClass = 'danger';
				}
			});
		} else {
			this.m_customerForm.get(this.LOGISTIC_AREA).setValue(this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE));
			this.m_customerForm.get(this.COUNTRY).setValue('DE');
		}
	}

	@Output() public exit: EventEmitter<boolean> = new EventEmitter();
	@Output() public customerSaved: EventEmitter<string> = new EventEmitter();

	@ViewChild('formDirective', { static: true }) public formDirective: FormGroupDirective;

	public readonly LOGISTIC_AREA_TYPE: LogisticsAreaType = LogisticsAreaType.CUSTOMER;
	public readonly HIERARCHY_NODES_1: string = 'hierarchyNode1';
	public readonly HIERARCHY_NODES_2: string = 'hierarchyNode2';
	public readonly HIERARCHY_NODES_3: string = 'hierarchyNode3';
	public readonly HIERARCHY_NODES_4: string = 'hierarchyNode4';
	public readonly LAST_NAME: string = 'lastName';
	public readonly FIRST_NAME: string = 'firstName';
	public readonly EMAIL: string = 'email';
	public readonly PHONE: string = 'phoneNumber';
	public readonly DISTRIBUTION_AMOUNT_1: string = 'distributionAmount1';
	public readonly DISTRIBUTION_AMOUNT_2: string = 'distributionAmount2';
	public readonly STREET: string = 'street';
	public readonly ZIP: string = "zip";
	public readonly CITY: string = "city";
	public readonly COUNTRY: string = "country";
	public readonly ID: string = "id";
	public readonly TITLE: string = "title";
	public readonly LOGISTIC_AREA: string = 'logisticArea';
	public readonly COMPANY_NAME1: string = 'companyName1';
	public readonly COMPANY_NAME2: string = 'companyName2';
	public readonly AT_LEAST_ONE: string = 'atLeastOne';
	public readonly PUBLIC_ADDRESS: string = 'publicAddress';
	public readonly OWNER: string = 'owner';

	private m_forCompany: boolean = false;
	private m_saved: boolean = false;
	private m_selectedCustomerOid: string = '';
	private m_selectedCustomer: CustomerByOid.Result;
	private m_configuration: Configuration.Result;
	private m_subscriptionsSubject = new Subject();
	public m_displayMessage: string;
	public m_displayError: HttpErrorResponse;
	public m_messageHandlerClass: string = 'info';
	public m_customerForm: UntypedFormGroup;

	constructor(
		private m_configurationService: ConfigurationService,
		private m_customerService: CustomerService,
		private m_logisticAreaService: LogisticAreaService,
		private m_formBuilder: UntypedFormBuilder,
		private m_translation: CustomerEditI18n,
		private loginService: LoginLibService
	) {
		this.m_customerForm = this.m_formBuilder.group({
			[this.AT_LEAST_ONE]: this.m_formBuilder.group({
				[this.LAST_NAME]: [''],
				[this.COMPANY_NAME1]: [''],
				[this.COMPANY_NAME2]: [''],
			}, { validators: this.atLeastOneValidator }),
			[this.LOGISTIC_AREA]: [this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE), Validators.required],
			[this.ID]: [''],
			[this.TITLE]: [''],
			[this.FIRST_NAME]: [''],
			[this.EMAIL]: [''],
			[this.PHONE]: [''],
			[this.STREET]: ['', Validators.required],
			[this.ZIP]: ['', Validators.required],
			[this.CITY]: ['', Validators.required],
			[this.COUNTRY]: ['', Validators.required],
			[this.HIERARCHY_NODES_1]: [],
			[this.DISTRIBUTION_AMOUNT_1]: [],
			[this.DISTRIBUTION_AMOUNT_2]: [],
			[this.HIERARCHY_NODES_2]: this.m_formBuilder.control({ value: '', disabled: true }),
			[this.HIERARCHY_NODES_3]: this.m_formBuilder.control({ value: '', disabled: true }),
			[this.HIERARCHY_NODES_4]: this.m_formBuilder.control({ value: '', disabled: true }),
			[this.OWNER]: this.m_formBuilder.control(''),
			[this.PUBLIC_ADDRESS]: this.m_formBuilder.control(false),
		});
	}

	public atLeastOneValidator: ValidatorFn = (p_control: UntypedFormGroup): ValidationErrors | null => {
		const controls = p_control.controls;
		if (controls) {
			const foundIndex = Object.keys(controls).findIndex((key) => controls[key].value != '');
			if (foundIndex === -1) {
				return { 'atLeastOneRequired': true };
			}
		}
	}

	/**
	 * die Konfiuration sowie die Hierarchiestufe 1 wird geladen
	 */
	public ngOnInit(): void {
		this.observeNodes();

		// Ausgewählte Konfiguration laden
		this.m_configurationService.getConfiguration().pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (data) => {
				this.m_configuration = data;
				if (this.m_configuration?.accessAllAddresses && (!this.m_selectedCustomer || this.m_selectedCustomer.owner?.oid == this.m_configuration?.userOid))
					this.m_customerForm.get(this.PUBLIC_ADDRESS).enable();
				else this.m_customerForm.get(this.PUBLIC_ADDRESS).disable();
				this.getControl(this.OWNER).setValue(this.m_configuration?.userName);
			},
			error: (error) => {
				this.m_displayError = error;
				this.m_messageHandlerClass = 'danger';
			}
		});
	}

	/**
	 * die Subscriptions werden geschlossen
	 */
	public ngOnDestroy(): void {
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	/**
	 * Customer wird gespeichert.
	 */
	public saveCustomer_Submit(): void {
		const newCustomer: IZbsApp.Customer = {
			lastName: this.getControl(this.AT_LEAST_ONE + '.' + this.LAST_NAME).value ? this.getControl(this.AT_LEAST_ONE + '.' + this.LAST_NAME).value : undefined,
			companyName1: this.getControl(this.AT_LEAST_ONE + '.' + this.COMPANY_NAME1).value ? this.getControl(this.AT_LEAST_ONE + '.' + this.COMPANY_NAME1).value : undefined,
			companyName2: this.getControl(this.AT_LEAST_ONE + '.' + this.COMPANY_NAME2).value ? this.getControl(this.AT_LEAST_ONE + '.' + this.COMPANY_NAME2).value : undefined,
			logisticsAreaProxyOid: this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE),
			id: this.getControl(this.ID).value ? this.getControl(this.ID).value : undefined,
			title: this.getControl(this.TITLE).value ? this.getControl(this.TITLE).value : undefined,
			firstName: this.getControl(this.FIRST_NAME).value ? this.getControl(this.FIRST_NAME).value : undefined,
			email: this.getControl(this.EMAIL).value ? this.getControl(this.EMAIL).value : undefined,
			phoneNumber: this.getControl(this.PHONE).value ? this.getControl(this.PHONE).value : undefined,
			street: this.getControl(this.STREET).value ? this.getControl(this.STREET).value : undefined,
			zip: this.getControl(this.ZIP).value ? this.getControl(this.ZIP).value : undefined,
			city: this.getControl(this.CITY).value ? this.getControl(this.CITY).value : undefined,
			country: this.getControl(this.COUNTRY).value ? this.getControl(this.COUNTRY).value : undefined,
			hierarchyNode1: this.getControl(this.HIERARCHY_NODES_1).value ? this.getControl(this.HIERARCHY_NODES_1).value : undefined,
			hierarchyNode2: this.getControl(this.HIERARCHY_NODES_2).value ? this.getControl(this.HIERARCHY_NODES_2).value : undefined,
			hierarchyNode3: this.getControl(this.HIERARCHY_NODES_3).value ? this.getControl(this.HIERARCHY_NODES_3).value : undefined,
			hierarchyNode4: this.getControl(this.HIERARCHY_NODES_4).value ? this.getControl(this.HIERARCHY_NODES_4).value : undefined,
			distributionAmount1: this.getControl(this.DISTRIBUTION_AMOUNT_1).value,
			distributionAmount2: this.getControl(this.DISTRIBUTION_AMOUNT_2).value,
			userProxyOid: this.m_configuration?.userOid,
			isManualAdr: true,
			oid: this.m_selectedCustomerOid,
			companyOid: this.m_configuration?.companyOid,
			isPublicAdr: this.getControl(this.PUBLIC_ADDRESS).value ? true : false
		};
		this.m_customerService.saveCustomer(newCustomer, this.m_configurationService.SelectedConfigOid).pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (data) => {
				this.m_saved = true;
				this.customerSaved.emit(this.m_selectedCustomerOid);
			},
			error: (error) => {
				this.m_displayError = error;
				this.m_messageHandlerClass = 'danger';
			}
		});
	}

	/**
	 * Wenn FormGroup verwendet wurde wird bei verlassen modaler Dialog angezeigt.
	 */
	public checkCustomerForm(): void {
		if (this.m_customerForm.touched == true && this.m_saved == false) {
			if (confirm(this.m_translation.customerEditDialogConfirm)) {
				this.initCustomerForm();
				this.exit.emit(true);
			} else {
				this.exit.emit(false);
				this.m_saved = false;
			}
		} else {
			this.initCustomerForm();
			this.exit.emit(true);
		}
	}

	/**
	 * FormGroup wird initialisiert gesetzt.
	 */
	public initCustomerForm(): void {
		this.m_saved = false;
		this.m_selectedCustomerOid = '';
		this.formDirective.resetForm();
		this.m_customerForm.reset();
		this.m_customerForm.get(this.LOGISTIC_AREA).setValue(this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE));
		this.m_customerForm.get(this.COUNTRY).setValue('DE');
		this.m_customerForm.get(this.OWNER).setValue(this.m_configuration?.userName);
		this.m_customerForm.get(this.AT_LEAST_ONE).get(this.LAST_NAME).setValue('');
		this.m_customerForm.get(this.AT_LEAST_ONE).get(this.COMPANY_NAME1).setValue('');
		this.m_customerForm.get(this.AT_LEAST_ONE).get(this.COMPANY_NAME2).setValue('');
	}

	/**
	 * Wird bei Änderung einer Hierarchiestufe aufgerufen, Hierarchiestufen werden abgewählt oder hinzugefügt
	 */
	public observeNodes(): void {
		this.getControl(this.HIERARCHY_NODES_1).valueChanges.pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (value) => {
				// Nachfolgende Felder bei Nichtauswahl deaktivieren
				if (value && value.length >= 1) {
					this.getControl(this.HIERARCHY_NODES_2).enable();
				} else {
					this.getControl(this.HIERARCHY_NODES_2).reset();
					this.getControl(this.HIERARCHY_NODES_2).disable();
					this.getControl(this.HIERARCHY_NODES_3).reset();
					this.getControl(this.HIERARCHY_NODES_3).disable();
					this.getControl(this.HIERARCHY_NODES_4).reset();
					this.getControl(this.HIERARCHY_NODES_4).disable();
				}
			}
		});

		this.getControl(this.HIERARCHY_NODES_2).valueChanges.pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (value) => {
				// Nachfolgende Felder bei Nichtauswahl deaktivieren
				if (value && value.length >= 1) {
					this.getControl(this.HIERARCHY_NODES_3).enable();
				} else {
					this.getControl(this.HIERARCHY_NODES_3).reset();
					this.getControl(this.HIERARCHY_NODES_3).disable();
					this.getControl(this.HIERARCHY_NODES_4).reset();
					this.getControl(this.HIERARCHY_NODES_4).disable();
				}
			}
		});

		this.getControl(this.HIERARCHY_NODES_3).valueChanges.pipe(
			takeUntil(this.m_subscriptionsSubject)
		).subscribe({
			next: (value) => {
				// Nachfolgende Felder bei Nichtauswahl deaktivieren
				if (value && value.length >= 1) {
					this.getControl(this.HIERARCHY_NODES_4).enable();
				} else {
					this.getControl(this.HIERARCHY_NODES_4).reset();
					this.getControl(this.HIERARCHY_NODES_4).disable();
				}
			}
		});
	}


	/**
	 * Setzt ein FormControl zurück.
	 * @param p_controlName FormControlName
	 */
	public clearInput_Click(p_controlName): void {
		this.getControl(p_controlName).setValue('');
	}

	/**
	 * FormGroup wird auf Defaultwerte gesetzt.
	 */
	public resetCustomerForm_Click(): void {
		this.formDirective.resetForm();
		this.m_customerForm.reset();
		this.getControl(this.LOGISTIC_AREA).setValue(this.m_logisticAreaService.getLogisticArea(this.LOGISTIC_AREA_TYPE));
		this.m_customerForm.get(this.COUNTRY).setValue('DE');
		this.m_customerForm.get(this.AT_LEAST_ONE).get(this.LAST_NAME).setValue('');
		this.m_customerForm.get(this.AT_LEAST_ONE).get(this.COMPANY_NAME1).setValue('');
		this.m_customerForm.get(this.AT_LEAST_ONE).get(this.COMPANY_NAME2).setValue('');
	}

	/**
	 * Gibt ein FormControl basierend auf dessen Name zurück.
	 * @param p_name FormControlName
	 */
	public getControl(p_name: string): AbstractControl {
		return this.m_customerForm.get(p_name);
	}

	/**
	 * Die Nachrichten des MessageHandlers werden zurückgesetzt.
	 */
	public clearDisplayMessages(): void {
		this.m_displayError = null;
		this.m_displayMessage = null;
	}

	/**
	 * Gibt einen boolean an das aufrufende Component für einen Tabwechsel.
	 */
	public tabBack(): void {
		this.exit.emit(true);
	}

}
